/**
 * Browser-side note store hook
 * Provides IndexedDB-backed read/write access to notes
 * Each identity gets its own isolated database
 */

import { useCallback, useEffect, useState, useRef } from 'react';
import { StoredNote, NotePayload, NoteCategory } from '@maco144/shared';

const DB_NAME_PREFIX = 'rising-sun-notes';
const STORE_NAME = 'notes';
const DB_VERSION = 1;

export interface NoteStoreState {
  isInitialized: boolean;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook for reading/writing notes to IndexedDB
 * Stores notes per identity for isolation
 */
export function useNoteStore(userId?: string) {
  const [state, setState] = useState<NoteStoreState>({
    isInitialized: false,
    isLoading: false,
    error: null,
  });

  // Keep reference to database for cleanup
  const dbRef = useRef<IDBDatabase | null>(null);

  // Get database name for this user
  const getDbName = useCallback((uid: string): string => {
    return `${DB_NAME_PREFIX}-${uid}`;
  }, []);

  // Open or get IndexedDB connection
  const getDB = useCallback(
    async (uid: string): Promise<IDBDatabase> => {
      if (dbRef.current) {
        return dbRef.current;
      }

      return new Promise((resolve, reject) => {
        const request = indexedDB.open(getDbName(uid), DB_VERSION);

        request.onupgradeneeded = (event) => {
          const db = (event.target as IDBOpenDBRequest).result;
          // Create notes store if it doesn't exist
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
            // Create indices for efficient queries
            store.createIndex('category', 'category', { unique: false });
            store.createIndex('noteType', 'noteType', { unique: false });
            store.createIndex('isPlanted', 'isPlanted', { unique: false });
            store.createIndex('archived', 'archived', { unique: false });
            store.createIndex('createdAt', 'createdAt', { unique: false });
            store.createIndex('tags', 'tags', { unique: false, multiEntry: true });
          }
        };

        request.onsuccess = () => {
          const db = request.result;
          dbRef.current = db;
          resolve(db);
        };

        request.onerror = () => {
          reject(new Error(`Failed to open IndexedDB: ${request.error}`));
        };
      });
    },
    [getDbName]
  );

  // Initialize store for a user
  const initialize = useCallback(
    async (uid: string) => {
      if (!uid) {
        setState((prev) => ({ ...prev, error: 'No user ID provided' }));
        return;
      }

      setState((prev) => ({ ...prev, isLoading: true }));
      try {
        await getDB(uid);
        setState((prev) => ({
          ...prev,
          isInitialized: true,
          isLoading: false,
          error: null,
        }));
      } catch (err) {
        const error = err instanceof Error ? err.message : 'Unknown error';
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error,
        }));
      }
    },
    [getDB]
  );

  // Initialize when userId changes
  useEffect(() => {
    if (userId) {
      initialize(userId);
    }
  }, [userId, initialize]);

  // Get a single note
  const getNote = useCallback(
    async (noteId: string): Promise<StoredNote | undefined> => {
      if (!userId || !state.isInitialized) return undefined;

      try {
        const db = await getDB(userId);
        return new Promise((resolve, reject) => {
          const tx = db.transaction([STORE_NAME], 'readonly');
          const store = tx.objectStore(STORE_NAME);
          const request = store.get(noteId);

          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        });
      } catch (err) {
        console.error(`Failed to get note ${noteId}:`, err);
        return undefined;
      }
    },
    [userId, state.isInitialized, getDB]
  );

  // Get all notes
  const getAllNotes = useCallback(async (): Promise<StoredNote[]> => {
    if (!userId || !state.isInitialized) return [];

    try {
      const db = await getDB(userId);
      return new Promise((resolve, reject) => {
        const tx = db.transaction([STORE_NAME], 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const request = store.getAll();

        request.onsuccess = () => resolve(request.result as StoredNote[]);
        request.onerror = () => reject(request.error);
      });
    } catch (err) {
      console.error('Failed to get all notes:', err);
      return [];
    }
  }, [userId, state.isInitialized, getDB]);

  // Set a single note
  const setNote = useCallback(
    async (noteId: string, payload: NotePayload, userId_param: string): Promise<void> => {
      if (!userId || !state.isInitialized) return;

      try {
        const db = await getDB(userId);

        const storedNote: StoredNote = {
          id: noteId,
          userId: userId_param || userId,
          creator: userId_param || userId,
          timestamp: Date.now(),
          ...payload,
          isSynced: false,
        };

        await new Promise<void>((resolve, reject) => {
          const tx = db.transaction([STORE_NAME], 'readwrite');
          const store = tx.objectStore(STORE_NAME);
          const request = store.put(storedNote);

          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
      } catch (err) {
        console.error(`Failed to set note ${noteId}:`, err);
      }
    },
    [userId, state.isInitialized, getDB]
  );

  // Delete a note
  const deleteNote = useCallback(
    async (noteId: string): Promise<void> => {
      if (!userId || !state.isInitialized) return;

      try {
        const db = await getDB(userId);

        await new Promise<void>((resolve, reject) => {
          const tx = db.transaction([STORE_NAME], 'readwrite');
          const store = tx.objectStore(STORE_NAME);
          const request = store.delete(noteId);

          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
      } catch (err) {
        console.error(`Failed to delete note ${noteId}:`, err);
      }
    },
    [userId, state.isInitialized, getDB]
  );

  // Get notes by category
  const getNotesByCategory = useCallback(
    async (category: NoteCategory): Promise<StoredNote[]> => {
      if (!userId || !state.isInitialized) return [];

      try {
        const db = await getDB(userId);
        return new Promise((resolve, reject) => {
          const tx = db.transaction([STORE_NAME], 'readonly');
          const store = tx.objectStore(STORE_NAME);
          const index = store.index('category');
          const request = index.getAll(category);

          request.onsuccess = () => resolve(request.result as StoredNote[]);
          request.onerror = () => reject(request.error);
        });
      } catch (err) {
        console.error(`Failed to get notes by category ${category}:`, err);
        return [];
      }
    },
    [userId, state.isInitialized, getDB]
  );

  // Get notes by tag
  const getNotesByTag = useCallback(
    async (tag: string): Promise<StoredNote[]> => {
      if (!userId || !state.isInitialized) return [];

      try {
        const db = await getDB(userId);
        return new Promise((resolve, reject) => {
          const tx = db.transaction([STORE_NAME], 'readonly');
          const store = tx.objectStore(STORE_NAME);
          const index = store.index('tags');
          const request = index.getAll(tag);

          request.onsuccess = () => resolve(request.result as StoredNote[]);
          request.onerror = () => reject(request.error);
        });
      } catch (err) {
        console.error(`Failed to get notes by tag ${tag}:`, err);
        return [];
      }
    },
    [userId, state.isInitialized, getDB]
  );

  // Get planted notes
  const getPlantedNotes = useCallback(async (): Promise<StoredNote[]> => {
    if (!userId || !state.isInitialized) return [];

    try {
      const db = await getDB(userId);
      return new Promise((resolve, reject) => {
        const tx = db.transaction([STORE_NAME], 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const index = store.index('isPlanted');
        const request = index.getAll(true);

        request.onsuccess = () => {
          const notes = request.result as StoredNote[];
          // Filter out archived notes
          resolve(notes.filter((n) => !n.archived));
        };
        request.onerror = () => reject(request.error);
      });
    } catch (err) {
      console.error('Failed to get planted notes:', err);
      return [];
    }
  }, [userId, state.isInitialized, getDB]);

  // Get archived notes
  const getArchivedNotes = useCallback(async (): Promise<StoredNote[]> => {
    if (!userId || !state.isInitialized) return [];

    try {
      const db = await getDB(userId);
      return new Promise((resolve, reject) => {
        const tx = db.transaction([STORE_NAME], 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const index = store.index('archived');
        const request = index.getAll(true);

        request.onsuccess = () => resolve(request.result as StoredNote[]);
        request.onerror = () => reject(request.error);
      });
    } catch (err) {
      console.error('Failed to get archived notes:', err);
      return [];
    }
  }, [userId, state.isInitialized, getDB]);

  // Export all notes
  const exportAll = useCallback(async (): Promise<Record<string, NotePayload>> => {
    if (!userId || !state.isInitialized) return {};

    try {
      const db = await getDB(userId);

      return new Promise((resolve, reject) => {
        const tx = db.transaction([STORE_NAME], 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const request = store.getAll();

        request.onsuccess = () => {
          const notes = request.result as StoredNote[];
          const result: Record<string, NotePayload> = {};
          for (const note of notes) {
            const {
              id,
              userId: _,
              creator,
              timestamp,
              isSynced,
              syncError,
              proof,
              ...payload
            } = note;
            result[id] = payload;
          }
          resolve(result);
        };

        request.onerror = () => reject(request.error);
      });
    } catch (err) {
      console.error('Failed to export notes:', err);
      return {};
    }
  }, [userId, state.isInitialized, getDB]);

  // Import notes
  const importAll = useCallback(
    async (data: Record<string, NotePayload>): Promise<void> => {
      if (!userId || !state.isInitialized) return;

      try {
        const db = await getDB(userId);

        await new Promise<void>((resolve, reject) => {
          const tx = db.transaction([STORE_NAME], 'readwrite');
          const store = tx.objectStore(STORE_NAME);

          for (const [noteId, payload] of Object.entries(data)) {
            const storedNote: StoredNote = {
              id: noteId,
              userId,
              creator: userId,
              timestamp: Date.now(),
              ...payload,
              isSynced: false,
            };
            store.put(storedNote);
          }

          tx.oncomplete = () => resolve();
          tx.onerror = () => reject(tx.error);
        });
      } catch (err) {
        console.error('Failed to import notes:', err);
      }
    },
    [userId, state.isInitialized, getDB]
  );

  // Clear all notes
  const clearAll = useCallback(async (): Promise<void> => {
    if (!userId || !state.isInitialized) return;

    try {
      const db = await getDB(userId);

      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction([STORE_NAME], 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const request = store.clear();

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (err) {
      console.error('Failed to clear notes:', err);
    }
  }, [userId, state.isInitialized, getDB]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (dbRef.current) {
        dbRef.current.close();
        dbRef.current = null;
      }
    };
  }, []);

  return {
    // State
    isInitialized: state.isInitialized,
    isLoading: state.isLoading,
    error: state.error,

    // Operations
    initialize,
    getNote,
    getAllNotes,
    setNote,
    deleteNote,
    getNotesByCategory,
    getNotesByTag,
    getPlantedNotes,
    getArchivedNotes,
    exportAll,
    importAll,
    clearAll,
  };
}
